import { Box } from '@mui/material'
import { BookingsResponseDto } from '@planning/app/api'
import { InitializationWrapper } from '@planning/components'
import { useWrappedLocalPagination } from '@planning/stores'
import { ISortDelegate } from '@planning/stores/PaginatedLocalStore'
import { SortingModel } from '@planning/stores/PaginatedStore'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { BookingsTable } from './Components/organisms/BookingsTable'
import { BookingsViewStore } from './Stores/BookingsViewStore'

export const bookingsSortingDelegate: ISortDelegate<BookingsResponseDto> = (sortingModel, a, b) => {
  const getValue = (item: BookingsResponseDto, sortingModel: SortingModel<BookingsResponseDto>) => {
    if (sortingModel.orderBy && _.has(item, sortingModel.orderBy)) {
      return _.get(item, sortingModel.orderBy)
    }

    return item.referenceNumber
  }

  const valA = getValue(a, sortingModel)
  const valB = getValue(b, sortingModel)
  let sort = 0
  if (typeof valA === 'string' && typeof valB === 'string') {
    sort = valA.localeCompare(valB)
  }

  // nulls sort after anything else
  if (valA !== valB) {
    if (valA === null || valA === undefined) {
      // asc nulls come first
      sort = -1
    } else if (valB === null || valB === undefined) {
      // desc nulls come last
      sort = 1
    }
  }

  if (sort === 0) {
    return sort
  }

  return sortingModel.isDescending ? -sort : sort
}

export const BookingsPage = observer(() => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const bookingsViewStore = useMemo(() => new BookingsViewStore(), [])
  const [showCompleted, setShowCompleted] = useState(false)

  useEffect(() => {
    bookingsViewStore.fetch()
  }, [bookingsViewStore])

  const filteredData = useMemo(() => {
    if (showCompleted) {
      return bookingsViewStore.filteredData.filter(item => item.isCompleted)
    }

    return bookingsViewStore.filteredData.filter(item => !item.isCompleted)
  }, [bookingsViewStore.filteredData, showCompleted])

  const bookingsListViewStore = useWrappedLocalPagination(
    () => filteredData,
    () => bookingsViewStore.fetch(),
    bookingsSortingDelegate,
  )

  return (
    <InitializationWrapper isInitializing={!bookingsViewStore.firstLoad}>
      <Box
        sx={{
          height: '100%',
          bgcolor: theme.palette.grey[100],
        }}
      >
        <Header title={t('Bookings', 'Bookings')} />

        <Box sx={{ padding: theme.customSpacing.l }}>
          <BookingsTable
            store={bookingsListViewStore}
            showCompleted={showCompleted}
            onShowCompletedChange={setShowCompleted}
          />
        </Box>
      </Box>
    </InitializationWrapper>
  )
})
